import React, { useEffect, useState } from 'react';

import LoadingSpinner from 'app-migration/components/loading/LoadingSpinner';
import usePathname from 'app-migration/hooks/usePathname';
import useRouter from 'app-migration/hooks/useRouter';
import { observer } from 'mobx-react';

import { Button } from 'DesignSystem2/Button';
import { Icon } from 'DesignSystem2/Icon';
import IconBadge from 'DesignSystem2/Icon/IconBadge';
import { Row, RowGroup } from 'DesignSystem2/Row';
import Text from 'DesignSystem2/Text';
import { Palette, Spacing } from 'DesignSystem2/Theme';
import { useStores } from 'hooks/use-stores';
import useDeposit from 'hooks/useDeposit';
import useInitiateBankDepositFlow from 'hooks/useInitiateBankDepositFlow';
import { FundMethods } from 'stores/ui/registration/registration';
import { toRoundedMoney } from 'utils/caseing';
import { createZeroHashHandler } from 'utils/crypto';
import { noop } from 'utils/noop';
import { openHelpCenterIsKalshiRegulated, openHelpCenterInterestApy } from 'utils/support';
import { formattedTimeToMidnightUTC } from 'utils/time';

interface DepositFundMethodSelectionStepProps {
    onNotNow: (e: React.FormEvent) => void;
    setLoading?: (loading: boolean) => void;
    canLinkBank?: boolean;
    isOrderPanel?: boolean;
    isDepositPopover?: boolean;
    onClose?: () => void;
}

const DepositFundMethodSelectionStep = ({
    onNotNow,
    setLoading,
    canLinkBank = true,
    isOrderPanel,
    isDepositPopover,
    onClose,
}: DepositFundMethodSelectionStepProps) => {
    const router = useRouter();
    const pathname = usePathname();

    const { uiStore, traderStore } = useStores();
    const userStore = traderStore.user;
    const paymentsStore = traderStore.payments;
    const dialog = uiStore.depositDialog;
    const bankStore = traderStore.bank;
    const updateSSNDialog = uiStore.updateSSNDialog;

    const registrationStore = uiStore.registration;

    const [timeToMidnight, setTimeToMidnight] = useState(formattedTimeToMidnightUTC());

    useEffect(
        () => {
            // Only record the metric if we're in the sign-up flow
            const isSignUp = pathname.includes('/sign-up');
            if (isSignUp) {
                userStore.metrics.recordSignUpMetric({
                    step: 'selectDepositMethod',
                });
            } else {
                userStore.metrics.recordDepositMetric({
                    step: 'openSelectDepositMethod',
                });
            }

            paymentsStore.loadDepositLimits();
            bankStore.loadDepositLimitInfo();
            registrationStore.load();

            const timer = setInterval(() => setTimeToMidnight(formattedTimeToMidnightUTC()), 1000);
            return (): void => clearTimeout(timer);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const individualAchLimit = bankStore.maxPlaidAchDepositAmountInCents;
    const individualAchMessage = !!individualAchLimit
        ? `${toRoundedMoney(individualAchLimit / 100)}`
        : paymentsStore.depositLimitsByRail['ach'] ?? '';

    const { openWireDialog } = useDeposit();
    const { onInitiateBankDepositFlow } = useInitiateBankDepositFlow({});

    const appendToText = dialog.eventTicker ? 'to trade on this market' : 'to add funds';

    const showCheckmarks = !isOrderPanel && !isDepositPopover;
    const documentVerificationRequired = registrationStore.kyc.documentRequired;
    const kycRequired = traderStore.user.isLoggedInApplicant;

    const checkRequirements = (fallback: () => void, isCrypto?: boolean) => {
        if (documentVerificationRequired) {
            dialog.open();
            dialog.setStep('kyc');
        } else if (kycRequired) {
            if (isCrypto) {
                uiStore.setShowCryptoAfterSignup(true);
            }
            router.push('/sign-up');
            dialog.close();
        } else {
            fallback();
        }
    };

    const noDebitCardLimit =
        !!bankStore.maxDebitCardDepositAmountCents && bankStore.maxDebitCardDepositAmountCents <= 0;
    const debitCardSecondaryText = noDebitCardLimit
        ? `Available again in ${timeToMidnight}`
        : `${paymentsStore.depositLimitsByRail['debit']} · Immediate`;
    const debitCardSecondaryColor = noDebitCardLimit ? Palette.Orange.x10 : Palette.Text.x20;

    const enableCryptoDeposit = uiStore.featureFlags.cryptoDepositEnabled;

    const loading = !bankStore.loadedLimit || !paymentsStore.loadedDepositLimitsByRail;
    if (loading) {
        return (
            <div className={'flex items-center justify-center h-[400px]'}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className={'flex flex-col gap-3 animate-fade-in'}>
            <div className={'flex flex-col gap-2'}>
                {showCheckmarks && (
                    <>
                        <div
                            style={{ display: 'flex', gap: Spacing(2), cursor: 'pointer' }}
                            onClick={() => openHelpCenterIsKalshiRegulated(uiStore, 'depositMethodSelection')}
                        >
                            <Icon.Check color={Palette.Brand.Primary} />
                            <Text.Body.x30>
                                {'Your cash is held and accessible in a federally regulated clearinghouse. '}
                                <Icon.InfoOutlined size={16} color={Palette.Fill.x30} />
                            </Text.Body.x30>
                        </div>
                        <div
                            style={{ display: 'flex', gap: Spacing(2), cursor: 'pointer' }}
                            onClick={() => openHelpCenterInterestApy(uiStore, 'depositMethodSelection')}
                        >
                            <Icon.Check color={Palette.Brand.Primary} />
                            <Text.Body.x30>
                                {`Earn variable interest (${uiStore.featureFlags.interestRate}) on your cash and positions. `}
                                <Icon.InfoOutlined size={16} color={Palette.Fill.x30} />
                            </Text.Body.x30>
                        </div>
                    </>
                )}
            </div>
            <div>
                {documentVerificationRequired ? (
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', gap: Spacing(2) }}>
                        <Text.Body.x20 color={Palette.Yes.x10}>
                            {`We'll need a photo of your ID to verify your account ${appendToText}.`}
                        </Text.Body.x20>
                    </div>
                ) : kycRequired ? (
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center', gap: Spacing(2) }}>
                        <Text.Body.x20 color={Palette.Yes.x10}>
                            {`We'll need more info to verify your account ${appendToText}.`}
                        </Text.Body.x20>
                    </div>
                ) : null}
                <RowGroup
                    firstRowDivider
                    lastRowDivider
                    rows={[
                        uiStore.featureFlags.isCardPaymentEnabled && (
                            <Row
                                key={'debit-card'}
                                labelText={'Debit card'}
                                className={'-my-px'}
                                labelTextIsBold
                                secondaryText={
                                    <Text.Body.x20 color={debitCardSecondaryColor}>
                                        {debitCardSecondaryText}
                                    </Text.Body.x20>
                                }
                                leadingComponent={
                                    <IconBadge
                                        iconName={'CreditCard'}
                                        color={'transparent'}
                                        iconColor={Palette.Text.x10}
                                    />
                                }
                                trailingIcon={'ChevronRight'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    checkRequirements(() => {
                                        dialog.reset();
                                        registrationStore.setFundMethod(FundMethods.DEBIT_CARD);
                                    });
                                }}
                                role={'button'}
                                title={'deposit-method-debit-card'}
                            />
                        ),
                        !!canLinkBank && (
                            <Row
                                key={'bank-transfer'}
                                labelText={'Bank transfer'}
                                className={'-my-px'}
                                labelTextIsBold
                                secondaryText={
                                    <Text.Body.x20 color={Palette.Text.x20}>
                                        {`${individualAchMessage} instant`}
                                    </Text.Body.x20>
                                }
                                leadingComponent={
                                    <IconBadge
                                        iconName={'AccountBalanceOutlined'}
                                        color={'transparent'}
                                        iconColor={Palette.Text.x10}
                                    />
                                }
                                trailingIcon={'ChevronRight'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    checkRequirements(() => {
                                        userStore.metrics.recordDepositMetric({
                                            step: 'bankSelected',
                                        });
                                        registrationStore.setFundMethod(FundMethods.BANK);
                                        onInitiateBankDepositFlow(pathname.includes('sign-up'));
                                        if (setLoading) {
                                            setLoading(true);
                                        }
                                    });
                                }}
                                role={'button'}
                                title={'deposit-method-bank-transfer'}
                            />
                        ),
                        enableCryptoDeposit && (
                            <Row
                                key={'crypto'}
                                labelText={'Crypto'}
                                className={'-my-px'}
                                secondaryText={
                                    <Text.Body.x20 color={Palette.Text.x20}>
                                        {paymentsStore.depositLimitsByRail['crypto'] ?? ''} · Below 30 minutes
                                    </Text.Body.x20>
                                }
                                labelTextIsBold
                                leadingComponent={
                                    <IconBadge
                                        iconName={'AutoRenew'}
                                        color={'transparent'}
                                        iconColor={Palette.Text.x10}
                                    />
                                }
                                trailingIcon={'ChevronRight'}
                                onClick={(e): void => {
                                    e.preventDefault();
                                    userStore.metrics.recordDepositMetric({
                                        step: 'cryptoSelected',
                                    });
                                    registrationStore.setFundMethod(FundMethods.CRYPTO);
                                    if (dialog.isOpen) {
                                        dialog.close();
                                    }
                                    onClose?.();

                                    // To deposit with Zero Hash user needs to have SSN verified
                                    const ssnNeeded = traderStore.user.ssnNeeded;
                                    if (ssnNeeded) {
                                        updateSSNDialog.open();
                                        return;
                                    }

                                    checkRequirements(() => {
                                        if (
                                            traderStore.user.user.state === 'NY' &&
                                            uiStore.featureFlags.blockNyZeroHash
                                        ) {
                                            uiStore.setShowZeroHashNyDialog(true);
                                        } else {
                                            traderStore.crypto.createTransfer(
                                                traderStore.user.getUserId,
                                                (token) => {
                                                    createZeroHashHandler(token);
                                                },
                                                noop,
                                                (error) => {
                                                    if (error.error.code === 'document_verification_required') {
                                                        router.push('/sign-up/identity');
                                                        return;
                                                    }
                                                },
                                            );
                                        }
                                    }, true);
                                }}
                                role={'button'}
                                title={'deposit-method-crypto'}
                            />
                        ),
                        <Row
                            key={'wire-transfer'}
                            labelText={'Wire transfer'}
                            labelTextIsBold
                            className={'-my-px'}
                            secondaryText={
                                <Text.Body.x20 color={Palette.Text.x20}>
                                    {paymentsStore.depositLimitsByRail['wire'] ?? ''} · Same business day or next
                                </Text.Body.x20>
                            }
                            leadingComponent={
                                <IconBadge iconName={'SyncAlt'} color={'transparent'} iconColor={Palette.Text.x10} />
                            }
                            trailingIcon={'ChevronRight'}
                            onClick={(e) => {
                                e.preventDefault();
                                userStore.metrics.recordDepositMetric({
                                    step: 'wireTransferSelected',
                                });
                                registrationStore.setFundMethod(FundMethods.WIRE);
                                openWireDialog();
                                onClose?.();
                            }}
                            role={'button'}
                            title={'deposit-method-wire-transfer'}
                        />,
                    ]}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing(2) }}>
                    <Button rank={'tertiary'} variant={'brand'} text={'Not now'} onClick={onNotNow} fullWidth />
                </div>
            </div>
        </div>
    );
};

export default observer(DepositFundMethodSelectionStep);
